import type { UseMutationResult } from '@tanstack/react-query';
import type {
  GooglePlaceDto,
  Location,
  ReputationOverviewDto,
  ReputationSummaryDto,
} from '@up2stay/up2promo-api-client';
import type { ReactNode } from 'react';

import { AutosaveStatusCue } from '@/core/components/ui/autosave-status-cue';
import type { AutosaveCueStatus } from '@/core/hooks/use-autosave-cue';
import type { LinkedProductSummary } from '@/core/hooks/use-location-linked-products';

import { LocationDetailExternalServicesTab } from './location-detail-external-services-tab';
import { LocationDetailInformationTab } from './location-detail-information-tab';
import { LocationDetailIntegrationsTab } from './location-detail-integrations-tab';
import {
  LocationDetailOverviewTab,
  type LocationOverviewTab,
  type OverviewHealthLabels,
} from './location-detail-overview-tab';
import type { LocationDetailDict } from './location-detail-types';
import type { LocationAddressDetails } from './location-details-form';
import type { LocationHealthSummary } from './location-health';
import type { LocationLinkEntry } from './location-link-entry.types';
import { LocationLinkedProductsBanner } from './location-linked-products-banner';
import type { LocationReviewInsightsLabels } from './location-review-insights';

export type LocationDetailTabItem = {
  name: string;
  value: string;
  content: ReactNode;
};

type LocationDetailTabsProps = {
  d: LocationDetailDict | undefined;
  locale: string;
  location: Location;
  workspaceId: string;
  locationHealth: LocationHealthSummary | null;
  linkedProducts: LinkedProductSummary[];
  linkedProductsDict: LocationDetailDict['linkedProducts'] | undefined;
  overviewHealthLabels: OverviewHealthLabels;
  showReviewInsights: boolean;
  reputationOverview: ReputationOverviewDto | undefined;
  reputationOverviewLoading: boolean;
  reputationSummary: ReputationSummaryDto | undefined;
  reputationSummaryLoading: boolean;
  reputationSummaryError: boolean;
  reviewInsightsLabels: LocationReviewInsightsLabels;
  onNavigateTab: (tab: LocationOverviewTab) => void;
  onViewProducts: () => void;
  onOpenReputation: () => void;
  onOpenInbox: () => void;
  editNameValue: string;
  onEditNameChange: (value: string) => void;
  onEditNameBlur: () => void;
  contactEmailValue: string;
  onContactEmailChange: (value: string) => void;
  contactEmailError?: string | null;
  contactPhoneValue: string;
  onContactPhoneChange: (value: string) => void;
  contactPhoneError?: string | null;
  onContactBlur: () => void;
  addressDetails: LocationAddressDetails | null;
  onAddressDetailsChange: (value: LocationAddressDetails | null) => void;
  onAddressBlur: () => void;
  latitude: number | undefined;
  longitude: number | undefined;
  onCoordinatesChange: (
    lat: number | undefined,
    lng: number | undefined,
  ) => void;
  onPlaceSelect: (place: GooglePlaceDto) => void;
  deleteMutation: UseMutationResult<void, Error, void, unknown>;
  autosaveStatus: AutosaveCueStatus;
  /** `locations:manage` — gates every editable control and the danger zone. */
  canManage: boolean;
  linkEntries: LocationLinkEntry[];
  onLinkEntriesChange: (
    entries:
      | LocationLinkEntry[]
      | ((prev: LocationLinkEntry[]) => LocationLinkEntry[]),
  ) => void;
  addLinkOpen: boolean;
  onAddLinkOpenChange: (open: boolean) => void;
  menuUrlError: string | null;
  onMenuUrlErrorChange: (error: string | null) => void;
  instagramError: string | null;
  onInstagramErrorChange: (error: string | null) => void;
  facebookError: string | null;
  onFacebookErrorChange: (error: string | null) => void;
  tiktokError: string | null;
  onTiktokErrorChange: (error: string | null) => void;
  googleConnected: boolean;
  updateListingMutation: UseMutationResult<
    void,
    Error,
    GooglePlaceDto,
    unknown
  >;
  clearListingMutation: UseMutationResult<void, Error, void, unknown>;
};

export function buildLocationDetailTabs({
  d,
  locale,
  location,
  workspaceId,
  locationHealth,
  linkedProducts,
  linkedProductsDict,
  overviewHealthLabels,
  showReviewInsights,
  reputationOverview,
  reputationOverviewLoading,
  reputationSummary,
  reputationSummaryLoading,
  reputationSummaryError,
  reviewInsightsLabels,
  onNavigateTab,
  onViewProducts,
  onOpenReputation,
  onOpenInbox,
  editNameValue,
  onEditNameChange,
  onEditNameBlur,
  contactEmailValue,
  onContactEmailChange,
  contactEmailError,
  contactPhoneValue,
  onContactPhoneChange,
  contactPhoneError,
  onContactBlur,
  addressDetails,
  onAddressDetailsChange,
  onAddressBlur,
  latitude,
  longitude,
  onCoordinatesChange,
  onPlaceSelect,
  deleteMutation,
  autosaveStatus,
  canManage,
  linkEntries,
  onLinkEntriesChange,
  addLinkOpen,
  onAddLinkOpenChange,
  menuUrlError,
  onMenuUrlErrorChange,
  instagramError,
  onInstagramErrorChange,
  facebookError,
  onFacebookErrorChange,
  tiktokError,
  onTiktokErrorChange,
  googleConnected,
  updateListingMutation,
  clearListingMutation,
}: LocationDetailTabsProps): LocationDetailTabItem[] {
  return [
    {
      name: d?.tabs?.overview ?? 'Übersicht',
      value: 'overview',
      content:
        locationHealth != null ? (
          <LocationDetailOverviewTab
            location={location}
            health={locationHealth}
            linkedProductCount={linkedProducts.length}
            onNavigateTab={onNavigateTab}
            onViewProducts={onViewProducts}
            dict={d}
            healthLabels={overviewHealthLabels}
            reviewInsights={
              showReviewInsights
                ? {
                    enabled: true,
                    overview: reputationOverview,
                    isLoading: reputationOverviewLoading,
                    summary: reputationSummary,
                    summaryLoading: reputationSummaryLoading,
                    summaryError: reputationSummaryError,
                    locale,
                    labels: reviewInsightsLabels,
                    onOpenReputation,
                    onOpenInbox,
                  }
                : undefined
            }
          />
        ) : null,
    },
    {
      name: d?.tabs?.information ?? 'Informationen',
      value: 'information',
      content: (
        <LocationDetailInformationTab
          location={location}
          editNameValue={editNameValue}
          onEditNameChange={onEditNameChange}
          onEditNameBlur={onEditNameBlur}
          contactEmailValue={contactEmailValue}
          onContactEmailChange={onContactEmailChange}
          contactEmailError={contactEmailError}
          contactPhoneValue={contactPhoneValue}
          onContactPhoneChange={onContactPhoneChange}
          contactPhoneError={contactPhoneError}
          onContactBlur={onContactBlur}
          addressDetails={addressDetails}
          onAddressDetailsChange={onAddressDetailsChange}
          onAddressBlur={onAddressBlur}
          latitude={latitude}
          longitude={longitude}
          onCoordinatesChange={onCoordinatesChange}
          onPlaceSelect={onPlaceSelect}
          deleteMutation={deleteMutation}
          autosaveStatus={autosaveStatus}
          canManage={canManage}
          dict={
            linkedProducts.length > 0
              ? {
                  ...d,
                  delete: {
                    ...d?.delete,
                    dialogDescription:
                      linkedProductsDict?.confirmDeleteLocation?.replace(
                        '{count}',
                        String(linkedProducts.length),
                      ) ?? d?.delete?.dialogDescription,
                  },
                }
              : d
          }
        />
      ),
    },
    {
      name: d?.tabs?.externalServices ?? 'Links',
      value: 'external-services',
      content: (
        <div className="flex flex-col gap-4">
          <AutosaveStatusCue
            status={autosaveStatus}
            labels={{
              idle: d?.autosave?.idle,
              saving: d?.autosave?.saving,
              saved: d?.autosave?.saved,
              error: d?.autosave?.error,
            }}
          />
          <LocationLinkedProductsBanner
            linkedProducts={linkedProducts}
            locationId={location.id}
            dict={linkedProductsDict}
          />
          <LocationDetailExternalServicesTab
            location={location}
            linkEntries={linkEntries}
            onLinkEntriesChange={onLinkEntriesChange}
            addLinkOpen={addLinkOpen}
            onAddLinkOpenChange={onAddLinkOpenChange}
            menuUrlError={menuUrlError}
            onMenuUrlErrorChange={onMenuUrlErrorChange}
            instagramError={instagramError}
            onInstagramErrorChange={onInstagramErrorChange}
            facebookError={facebookError}
            onFacebookErrorChange={onFacebookErrorChange}
            tiktokError={tiktokError}
            onTiktokErrorChange={onTiktokErrorChange}
            dict={d}
          />
        </div>
      ),
    },
    {
      name: d?.tabs?.integrations ?? 'Integrationen',
      value: 'integrations',
      content: (
        <div className="flex flex-col gap-4">
          <LocationLinkedProductsBanner
            linkedProducts={linkedProducts}
            locationId={location.id}
            dict={linkedProductsDict}
          />
          <LocationDetailIntegrationsTab
            location={location}
            workspaceId={workspaceId}
            googleConnected={googleConnected}
            updateListingMutation={updateListingMutation}
            clearListingMutation={clearListingMutation}
            dict={d}
          />
        </div>
      ),
    },
  ];
}
